import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { reatomEditWallet } from '@root/features/wallet'
import {
  Drawer,
  FadeOverflow,
  hiddenScrollbarCss,
  IgnoreDrawerSwipe,
  SaveIcon,
} from '@root/shared/ui-kit'

import { WalletColorSwatch } from '../../wallet-color-swatch/ui/wallet-color-swatch'

import { WalletColorPreview } from './wallet-color-preview'

import type { WalletModel } from '@root/entities/wallet/atoms'
import type { DrawerProps } from '@root/shared/ui-kit'

type WalletColorDrawerProps = Pick<DrawerProps, 'onOpenChange' | 'open'> & {
  wallet: WalletModel
}

export const WalletColorDrawer: React.FC<WalletColorDrawerProps> = ({
  open,
  onOpenChange,
  wallet,
}) => {
  const { t } = useTranslation()
  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomEditWallet({ wallet }),
    [wallet],
  )
  const [state] = useAtom(model)
  const updateWallet = useAction(model.execute)
  const [currentColor] = useAtom(wallet.color)
  const [color, setColor] = useState(currentColor)

  const handleSave = () => {
    updateWallet({ color: color })
  }

  useEffect(() => {
    if (AsyncStateN.isSuccess(state)) {
      onOpenChange?.(false)
    }
  }, [onOpenChange, state])

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('wallet.wallet_color')}
    >
      <Body>
        <IgnoreDrawerSwipe>
          <SwatchContainer mode="always">
            <WalletColorSwatch
              currentColor={currentColor}
              value={color}
              onChange={setColor}
              size="m"
            />
          </SwatchContainer>
        </IgnoreDrawerSwipe>
        <Content>
          <WalletColorPreview color={color} wallet={wallet} />
          <Button
            fullWidth
            iconLeft={<SaveIcon />}
            size="m"
            color="primary"
            radius="rounded"
            busy={state.loading}
            disabled={currentColor === color}
            onClick={handleSave}
          >
            {t('common.save')}
          </Button>
        </Content>
      </Body>
    </Drawer>
  )
}

const Body = styled.div`
  padding-block: 8px var(--safe-bottom);
`

const Content = styled.div`
  padding-inline: 12px;
`

const SwatchContainer = styled(FadeOverflow)`
  margin-bottom: 8px;
  overflow-x: auto;
  display: grid;

  ${hiddenScrollbarCss}
`
